import { useState } from "react";
import { SWRConfig } from "swr";
import {
  ENVIRONMENT_PROVIDER_CONNECTION_LIFECYCLE_PROOF,
  EnvironmentConsole,
  type OperationAuthorityCaptureField,
  type OperationAuthorityCaptureState,
  type ProviderConnectionLifecycleAction,
  type ProviderConnectionLifecycleActionState,
} from "@excellent/ui";
import { apiCall, useApi } from "@excellent/ui/api";
import {
  environments,
  type EnvironmentOperationHandleResponse,
  type ProviderConnectionLifecycleResponse,
} from "@excellent/ui/api/contracts/environments";
import { verificationCanonicalClients } from "../../api/canonical-clients";
import { useCanonicalEnvironment } from "../../canonical-environment-provider";
import { EnvironmentDirectory } from "@excellent/ui/blocks/excellent/environments/environment-control";
import {
  environmentContextAllowsDiagnostics,
  useEnvironmentContextClock,
} from "@excellent/ui/blocks/excellent/environments/context-lifetime";
import { ActionButton } from "../../ui";
import { PageError, PageFrame, type VerificationPageProps } from "../page-frame";

export function EnvironmentsPage(props: VerificationPageProps) {
  const context = useCanonicalEnvironment();
  const [provider] = useState(() => () => new Map());
  // Drop diagnostics and prepared form state when access, identity or epochs
  // change. Never restore another context's URL-keyed diagnostics response.
  return (
    <SWRConfig key={context.cacheKey ?? "unavailable"} value={{ provider }}>
      <EnvironmentsPageContent {...props} />
    </SWRConfig>
  );
}

function EnvironmentsPageContent({ onNavigate }: VerificationPageProps) {
  const context = useCanonicalEnvironment();
  const now = useEnvironmentContextClock(context.response);
  const contextIsCurrent = environmentContextAllowsDiagnostics(
    context.response,
    Boolean(context.error),
    now,
  );
  const listApi = useApi(
    verificationCanonicalClients.environmentsList.operation,
    contextIsCurrent ? { query: { scope: "all" } } : null,
  );
  const d1AdmissionApi = useApi(environments.d1Admission, contextIsCurrent ? {} : null);
  const [providerConnectionId, setProviderConnectionId] = useState<string | null>(null);
  const [providerPlanId, setProviderPlanId] = useState<string | null>(null);
  const [providerLifecycleBusy, setProviderLifecycleBusy] = useState<string | null>(null);
  const [providerLifecycleError, setProviderLifecycleError] = useState<string | null>(null);
  const [providerLifecycleResult, setProviderLifecycleResult] =
    useState<ProviderConnectionLifecycleResponse | null>(null);
  const [operationAuthorityCapture, setOperationAuthorityCapture] =
    useState<OperationAuthorityCaptureState>({});
  const [operationResult, setOperationResult] = useState<EnvironmentOperationHandleResponse | null>(
    null,
  );

  function updateOperationAuthorityCapture(field: OperationAuthorityCaptureField, value: string) {
    setOperationAuthorityCapture((current) => ({ ...current, [field]: value }));
  }

  function captured(field: OperationAuthorityCaptureField): string | null {
    const value = operationAuthorityCapture[field]?.trim();
    return value ? value : null;
  }

  function rememberProviderLifecycleResult(result: ProviderConnectionLifecycleResponse) {
    setProviderLifecycleResult(result);
    if (result.planId) setProviderPlanId(result.planId);
    if (result.providerConnectionId) {
      setProviderConnectionId(result.providerConnectionId);
    } else if (!providerConnectionId && result.connectionIds[0]) {
      setProviderConnectionId(result.connectionIds[0]);
    }
  }

  async function runProviderConnectionAction(action: ProviderConnectionLifecycleAction) {
    setProviderLifecycleBusy(action.actionId);
    setProviderLifecycleError(null);
    try {
      const idempotencyKey = `human-console:${action.actionId}:${crypto.randomUUID()}`;
      let result: ProviderConnectionLifecycleResponse;
      switch (action.actionId) {
        case "environment.provider-connections.list":
          result = await apiCall(environments.providerConnections.list, {});
          break;
        case "environment.provider-connection.detail":
          if (!providerConnectionId)
            throw new Error("Select or execute a provider connection first.");
          result = await apiCall(environments.providerConnections.detail, {
            params: { connectionId: providerConnectionId },
          });
          break;
        case "environment.provider-connection.authorize.plan":
          result = await apiCall(environments.providerConnections.authorizationPlan, {
            body: { provider: "local-provider", idempotencyKey },
          });
          break;
        case "environment.provider-connection.authorize.execute":
          if (!providerPlanId) throw new Error("Create an authorization plan first.");
          result = await apiCall(environments.providerConnections.executePlan, {
            params: { planId: providerPlanId },
            body: { idempotencyKey },
          });
          break;
        case "environment.provider-connection.reauthorize.plan":
          if (!providerConnectionId)
            throw new Error("Execute or select a provider connection first.");
          result = await apiCall(environments.providerConnections.reauthorizationPlan, {
            params: { connectionId: providerConnectionId },
            body: { idempotencyKey, reason: "human_console_rotation" },
          });
          break;
        case "environment.provider-connection.revoke.plan":
          if (!providerConnectionId)
            throw new Error("Execute or select a provider connection first.");
          result = await apiCall(environments.providerConnections.revocationPlan, {
            params: { connectionId: providerConnectionId },
            body: { idempotencyKey, reason: "human_console_revocation" },
          });
          break;
        case "environment.provider-connection.inventory-refresh":
          if (!providerConnectionId)
            throw new Error("Execute or select a provider connection first.");
          result = await apiCall(environments.providerConnections.inventoryRefresh, {
            params: { connectionId: providerConnectionId },
            body: { idempotencyKey },
          });
          break;
        case "environment.provider-connection.expired-claim.refuse":
          if (!providerConnectionId)
            throw new Error("Execute or select a provider connection first.");
          {
            const providerCapabilityClaimDigest = captured("providerCapabilityClaimDigest");
            if (!providerCapabilityClaimDigest) {
              throw new Error("Capture the expired provider capability claim digest.");
            }
            result = await apiCall(environments.providerConnections.expiredClaimRefusal, {
              params: { connectionId: providerConnectionId },
              body: {
                idempotencyKey,
                providerCapabilityClaimDigest,
                reason: "expired_claim",
              },
            });
          }
          break;
        case "environment.operation.cancel": {
          const operationId = captured("operationId");
          if (!operationId) throw new Error("Bind an operation handle first.");
          const cancelled = await apiCall(environments.operations.cancel, {
            params: { operationId },
            body: {
              idempotencyKey,
              reason: "human_console_revocation",
              remainingStageDisposition: "in-flight-external-write",
            },
          });
          setOperationResult(cancelled);
          return;
        }
        case "environment.operation.reconcile": {
          const operationId = captured("operationId");
          const authorizationId = captured("authorizationId");
          const canonicalSignedAuthorizationBytes = captured("canonicalSignedAuthorizationBytes");
          const canonicalSignedTransitionBytes = captured("canonicalSignedTransitionBytes");
          const canonicalSignedTerminalEffectBytes = captured("canonicalSignedTerminalEffectBytes");
          const canonicalSignedNextUnresolvedOutcomeBytes = captured(
            "canonicalSignedNextUnresolvedOutcomeBytes",
          );
          if (!operationId) throw new Error("Bind an operation handle first.");
          if (!authorizationId) throw new Error("Capture the reconciliation authorization id.");
          if (!canonicalSignedAuthorizationBytes) {
            throw new Error("Capture canonical signed authorization bytes.");
          }
          if (!canonicalSignedTransitionBytes) {
            throw new Error("Capture canonical signed transition bytes.");
          }
          if (!canonicalSignedTerminalEffectBytes && !canonicalSignedNextUnresolvedOutcomeBytes) {
            throw new Error(
              "Capture signed terminal effect bytes or signed next unresolved outcome bytes.",
            );
          }
          const reconciled = await apiCall(environments.operations.reconcile, {
            params: { operationId },
            body: {
              idempotencyKey,
              authorizationId,
              canonicalSignedAuthorizationBytes,
              canonicalSignedTransitionBytes,
              canonicalSignedTerminalEffectBytes,
              canonicalSignedNextUnresolvedOutcomeBytes,
            },
          });
          setOperationResult(reconciled);
          return;
        }
        default:
          throw new Error(`${action.actionId} requires operation-specific authority evidence.`);
      }
      rememberProviderLifecycleResult(result);
    } catch (cause) {
      setProviderLifecycleError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setProviderLifecycleBusy(null);
    }
  }

  function providerConnectionActionState(
    action: ProviderConnectionLifecycleAction,
  ): ProviderConnectionLifecycleActionState {
    if (providerLifecycleBusy) {
      return {
        disabled: true,
        reason:
          providerLifecycleBusy === action.actionId
            ? "provider lifecycle action is running"
            : "another provider lifecycle action is running",
      };
    }
    if (
      action.actionId === "environment.provider-connection.authorize.execute" &&
      !providerPlanId
    ) {
      return { disabled: true, reason: "create an authorization plan first" };
    }
    if (
      [
        "environment.provider-connection.detail",
        "environment.provider-connection.reauthorize.plan",
        "environment.provider-connection.revoke.plan",
        "environment.provider-connection.inventory-refresh",
        "environment.provider-connection.expired-claim.refuse",
      ].includes(action.actionId) &&
      !providerConnectionId
    ) {
      return { disabled: true, reason: "execute or select a provider connection first" };
    }
    if (
      action.actionId === "environment.provider-connection.expired-claim.refuse" &&
      !captured("providerCapabilityClaimDigest")
    ) {
      return { disabled: true, reason: "requires expired provider capability claim digest" };
    }
    if (action.actionId === "environment.operation.cancel") {
      if (!captured("operationId")) {
        return { disabled: true, reason: "requires an operation handle selected from the ledger" };
      }
      return {};
    }
    if (action.actionId === "environment.operation.reconcile") {
      if (!captured("operationId")) {
        return { disabled: true, reason: "requires an operation handle selected from the ledger" };
      }
      if (
        !captured("authorizationId") ||
        !captured("canonicalSignedAuthorizationBytes") ||
        !captured("canonicalSignedTransitionBytes") ||
        (!captured("canonicalSignedTerminalEffectBytes") &&
          !captured("canonicalSignedNextUnresolvedOutcomeBytes"))
      ) {
        return { disabled: true, reason: "requires signed reconciliation authority evidence" };
      }
      return {};
    }
    return {};
  }

  return (
    <PageFrame
      page="environments"
      title="Environments"
      lede="Choose where you work and check the latest connection status."
      wide
      actions={
        <ActionButton
          actionId="environments.refresh"
          onClick={() => {
            void context.refresh().catch(() => {});
            void listApi.mutate();
          }}
        >
          Refresh
        </ActionButton>
      }
    >
      <EnvironmentDirectory
        key={context.requestGeneration}
        response={context.response}
        failed={Boolean(context.error)}
        invalidRoute={context.invalidRoute}
        onSelect={(subject) => context.select({ kind: "environment", subject })}
        onSelectAll={() => context.select({ kind: "all" })}
        onRefresh={() => {
          void context.refresh().catch(() => {});
        }}
        onNavigate={onNavigate}
      />
      {contextIsCurrent ? (
        <details>
          <summary className="cursor-pointer p-3 text-sm">
            Environment diagnostics and connection operations
          </summary>
          {listApi.error ? (
            <PageError
              title="Connection details unavailable"
              error={listApi.error}
              onRetry={() => void listApi.mutate()}
            />
          ) : null}
          {providerLifecycleResult || providerLifecycleError ? (
            <div
              className="rounded-md border border-border bg-background p-3 type-subtext text-muted-foreground"
              data-provider-connection-lifecycle-result=""
            >
              {providerLifecycleResult ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>Outcome: {providerLifecycleResult.lifecycleOutcome}</span>
                  <span>Digest: {providerLifecycleResult.canonicalDigest}</span>
                  <span>Plan: {providerLifecycleResult.planId ?? "none"}</span>
                  <span>Connection: {providerLifecycleResult.providerConnectionId ?? "none"}</span>
                  <span>
                    Claim:{" "}
                    {providerLifecycleResult.attemptedProviderCapabilityClaimDigest ?? "none"}
                  </span>
                </div>
              ) : null}
              {providerLifecycleError ? (
                <div className="mt-1 text-destructive">{providerLifecycleError}</div>
              ) : null}
            </div>
          ) : null}
          {operationResult ? (
            <div
              className="rounded-md border border-border bg-background p-3 type-subtext text-muted-foreground"
              data-operation-authority-result=""
            >
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span>Operation: {operationResult.operationId}</span>
                <span>State: {operationResult.state}</span>
                <span>Digest: {operationResult.canonicalDigest}</span>
                <span>Next: {operationResult.nextActionCode}</span>
              </div>
            </div>
          ) : null}
          <EnvironmentConsole
            list={listApi.data}
            d1Admission={d1AdmissionApi.data}
            d1AdmissionError={
              d1AdmissionApi.error instanceof Error ? d1AdmissionApi.error.message : null
            }
            providerConnectionLifecycle={ENVIRONMENT_PROVIDER_CONNECTION_LIFECYCLE_PROOF}
            onProviderConnectionAction={(action) => void runProviderConnectionAction(action)}
            providerConnectionActionState={providerConnectionActionState}
            operationAuthorityCapture={operationAuthorityCapture}
            onOperationAuthorityCaptureChange={updateOperationAuthorityCapture}
          />
        </details>
      ) : null}
    </PageFrame>
  );
}
